// src/components/Organograma/ColaboradorPopover.tsx
import { UserIcon, ViewColumnsIcon, XMarkIcon, DocumentCheckIcon } from '@heroicons/react/24/outline';
import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import type { Colaborador, Departamento, Flow } from '../../types';

interface ColaboradorPopoverProps {
  colaborador: Colaborador;
  departamentos: Departamento[];
  flows: Flow[];
  onClose: () => void;
  onViewProfile: (colaborador: Colaborador) => void;
  onNavigateToFlow?: (flow: Flow) => void;
  userPermissions?: {
    canEdit: boolean;
    canAdmin: boolean;
    currentUser?: any;
  };
}

const getColorFromString = (str: string) => {
  const colors = [
    'bg-blue-500', 'bg-red-500', 'bg-yellow-500', 'bg-green-500',
    'bg-purple-500', 'bg-pink-500', 'bg-indigo-500', 'bg-orange-500'
  ];
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

const getInitials = (name: string) => {
  return name
    .split(' ')
    .map(word => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

const ColaboradorPopover: React.FC<ColaboradorPopoverProps> = ({
  colaborador,
  departamentos,
  flows,
  onClose,
  onViewProfile,
  onNavigateToFlow,
  userPermissions
}) => {
  const [procedimentos, setProcedimentos] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const departamento = departamentos.find(dep => dep.id === colaborador.departamentoId);
  const superior = departamento?.responsavel === colaborador.id;
  
  const fluxosParticipante = flows.filter(flow => {
    if (flow.responsavelId === colaborador.id) return true;
    return flow.etapas.some(etapa => etapa.participantes?.includes(colaborador.id));
  });

  useEffect(() => {
    const carregarProcedimentos = async () => {
      try {
        setLoading(true);
        const response = await fetch('/api/procedimentos-simples');
        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            const procedimentosColaborador = (data.data || []).filter((proc: any) => 
              proc.colaboradores && proc.colaboradores.includes(colaborador.id)
            );
            setProcedimentos(procedimentosColaborador);
          }
        }
      } catch (error) {
        console.error('Erro ao carregar procedimentos:', error);
      } finally {
        setLoading(false);
      }
    };

    carregarProcedimentos();
  }, [colaborador.id]);

  const modalContent = (
    <>
      <div className="fixed inset-0 bg-black/30 z-40" onClick={onClose} />
      
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[80vh] overflow-hidden pointer-events-auto">
          
          <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
            <div className="flex items-center gap-2">
              <UserIcon className="h-5 w-5 text-gray-600" />
              <h3 className="font-semibold text-gray-900">Perfil do Colaborador</h3>
            </div>
            <button
              onClick={onClose}
              className="p-1 hover:bg-gray-200 rounded text-gray-500 hover:text-gray-700 transition-colors"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          <div className="max-h-[calc(80vh-150px)] overflow-y-auto">
            <div className="p-4">
              <div className="flex items-start gap-3">
                <div 
                  className={`w-12 h-12 rounded-lg ${getColorFromString(colaborador.nome)} 
                    text-white flex items-center justify-center text-lg font-semibold overflow-hidden`}
                >
                  {(colaborador as any)?.foto ? (
                    <img
                      src={(colaborador as any).foto}
                      alt={`Foto de ${colaborador.nome}`}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    getInitials(colaborador.nome)
                  )}
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900">{colaborador.nome}</h4>
                  <p className="text-sm text-gray-600">{colaborador.cargo}</p>
                  <div className="mt-1.5 flex items-center gap-2">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      colaborador.status === 'ativo' 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-gray-100 text-gray-700'
                    }`}>
                      {colaborador.status === 'ativo' ? 'Ativo' : 'Inativo'}
                    </span>
                    {superior && (
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded">
                        Gestor
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-4 grid grid-cols-2 gap-3">
                <div className="p-3 bg-gray-50 rounded border border-gray-200">
                  <p className="text-xs text-gray-500 mb-0.5">Departamento</p>
                  <p className="text-sm font-medium text-gray-900">{departamento?.nome}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded border border-gray-200">
                  <p className="text-xs text-gray-500 mb-0.5">Contratação</p>
                  <p className="text-sm font-medium text-gray-900">
                    {new Date(colaborador.dataContratacao).toLocaleDateString('pt-BR')}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="p-4 border-t border-gray-200">
              <div className="flex items-center gap-2 mb-3">
                <ViewColumnsIcon className="h-4 w-4 text-gray-600" />
                <h4 className="font-medium text-gray-900 text-sm">Fluxos</h4>
                {fluxosParticipante.length > 0 && (
                  <span className="ml-auto px-1.5 py-0.5 bg-gray-100 text-gray-700 text-xs rounded">
                    {fluxosParticipante.length}
                  </span>
                )}
              </div>
              {fluxosParticipante.length === 0 ? (
                <div className="text-center py-4 bg-gray-50 rounded border border-dashed border-gray-300">
                  <ViewColumnsIcon className="h-6 w-6 text-gray-400 mx-auto mb-1" />
                  <p className="text-xs text-gray-500">Não participa de nenhum fluxo</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {fluxosParticipante.map(flow => (
                    <button
                      key={flow.id}
                      onClick={() => onNavigateToFlow?.(flow)}
                      className="w-full flex items-center gap-2 p-2 text-left hover:bg-gray-50 border border-gray-200 rounded transition-colors"
                    >
                      <ViewColumnsIcon className="h-4 w-4 text-gray-500" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate">
                          {flow.name}
                        </div>
                        {flow.responsavelId === colaborador.id && (
                          <div className="text-xs text-gray-600 mt-0.5">
                            Responsável
                          </div>
                        )}
                      </div>
                      <div className="text-gray-400">→</div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="p-4 border-t border-gray-200">
              <div className="flex items-center gap-2 mb-3">
                <DocumentCheckIcon className="h-4 w-4 text-gray-600" />
                <h4 className="font-medium text-gray-900 text-sm">Procedimentos</h4>
                {procedimentos.length > 0 && (
                  <span className="ml-auto px-1.5 py-0.5 bg-gray-100 text-gray-700 text-xs rounded">
                    {procedimentos.length}
                  </span>
                )}
              </div>
              {loading ? (
                <div className="text-center py-4 bg-gray-50 rounded">
                  <div className="animate-spin h-6 w-6 border-2 border-gray-300 border-t-gray-600 rounded-full mx-auto"></div>
                  <p className="text-xs text-gray-500 mt-2">Carregando...</p>
                </div>
              ) : procedimentos.length === 0 ? (
                <div className="text-center py-4 bg-gray-50 rounded border border-dashed border-gray-300">
                  <DocumentCheckIcon className="h-6 w-6 text-gray-400 mx-auto mb-1" />
                  <p className="text-xs text-gray-500">Sem procedimentos vinculados</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {procedimentos.slice(0, 3).map(proc => (
                    <div
                      key={proc.id}
                      className="flex items-start gap-2 p-2 bg-gray-50 border border-gray-200 rounded"
                    >
                      <DocumentCheckIcon className="h-4 w-4 text-gray-500 flex-shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900">
                          {proc.nome}
                        </div>
                        {proc.objetivo && (
                          <div className="text-xs text-gray-600 mt-0.5 line-clamp-1">
                            {proc.objetivo}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  {procedimentos.length > 3 && (
                    <div className="text-center py-1 text-xs text-gray-500">
                      +{procedimentos.length - 3} outro{procedimentos.length - 3 !== 1 ? 's' : ''}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          
          <div className="p-4 bg-gray-50 border-t border-gray-200">
            <button
              onClick={() => onViewProfile(colaborador)}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gray-900 hover:bg-gray-800 text-white text-sm font-medium rounded transition-colors"
            >
              <UserIcon className="h-4 w-4" />
              <span>{(userPermissions?.canEdit || userPermissions?.canAdmin) ? 'Editar Perfil' : 'Ver Perfil Completo'}</span>
            </button>
          </div>
        </div>
      </div>
    </>
  );

  return createPortal(modalContent, document.body);
};

export default ColaboradorPopover;